import React, { useCallback, useMemo, useState } from 'react';
import { arrayOf, func, shape, number, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Input, Modal, Table, Button } from 'antd';
import { useSearch } from '@hooks';

import '../StagedRules/style.less';

const filterFunc = (value, comparator) => comparator(value.name);

function SelectCTAModal({ callToActionList, selectedCTAIds, onCancel, onOk }) {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const columns = useMemo(
    () => [
      {
        title: t('pathways:ProcedurePathways.wizard.careTeamRules.ctaModal.columns.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        width: 200,
      },
      {
        title: t('pathways:ProcedurePathways.wizard.careTeamRules.ctaModal.columns.type'),
        dataIndex: 'type',
        key: 'type',
        width: 150,
        render: type => {
          const typeKey = `pathways:ProcedurePathways.wizard.careTeamRules.callToAction.types.${type}`;
          return t(typeKey);
        },
      },
      {
        title: t('pathways:ProcedurePathways.wizard.careTeamRules.ctaModal.columns.value'),
        dataIndex: 'value',
        key: 'value',
        ellipsis: true,
      },
      {
        title: t('pathways:ProcedurePathways.wizard.careTeamRules.ctaModal.columns.audience'),
        dataIndex: 'audience',
        key: 'audience',
        width: 120,
        render: audience => {
          const audienceKey = `pathways:ProcedurePathways.wizard.careTeamRules.callToAction.audience.${audience}`;
          return t(audienceKey);
        },
      },
    ],
    [t],
  );

  const onOkClick = useCallback(() => {
    const selectedCTAs = callToActionList.filter(cta => selectedRowKeys.includes(cta.id));
    onOk(selectedCTAs);
    setSelectedRowKeys([]);
    onCancel();
  }, [callToActionList, selectedRowKeys, onCancel, onOk]);

  const [searchResults, searchProps] = useSearch(callToActionList, filterFunc);

  const dataSource = useMemo(() => {
    return searchResults.filter(cta => !selectedCTAIds.has(cta.id));
  }, [searchResults, selectedCTAIds]);

  return (
    <Modal
      className="select-rules-modal"
      destroyOnClose
      title={t('pathways:ProcedurePathways.wizard.careTeamRules.ctaModal.title')}
      visible
      onCancel={onCancel}
      footer={[
        <div className="footer-left" key="footer-button-select-all" />,
        <div className="footer-right" key="footer-button-add">
          <Button disabled={!selectedRowKeys.length} type="primary" onClick={onOkClick}>
            {t('common:selectionModal.add')}
          </Button>
        </div>,
      ]}
    >
      <Input.Search placeholder={t('common:search')} {...searchProps} />
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={{ position: 'bottom' }}
        rowKey="id"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
      />
    </Modal>
  );
}

SelectCTAModal.propTypes = {
  callToActionList: arrayOf(
    shape({
      id: number.isRequired,
      name: string.isRequired,
      type: string.isRequired,
      value: string.isRequired,
      audience: string.isRequired,
    }),
  ),
  selectedCTAIds: shape({
    has: func.isRequired,
  }).isRequired,
  onCancel: func.isRequired,
  onOk: func.isRequired,
};

SelectCTAModal.defaultProps = {
  callToActionList: [],
};

export default SelectCTAModal;

